import { useEffect, useRef, useState } from 'react';
import type { ClientRoomState } from '@hitster/shared';
import { useT } from '../i18n.tsx';
import type { RoomApi } from '../net/useRoom.ts';
import { IconMore } from './Icon.tsx';

/**
 * Raum-Menue mit den Aktionen, die eine Partie beenden.
 *
 * Bewusst hinter einem Ausklapp-Menue und mit Rueckfrage: "Spiel
 * abbrechen" und "Raum verlassen" sind nicht rueckgaengig zu machen und
 * duerfen nicht versehentlich getroffen werden -- schon gar nicht auf
 * einem Handy neben den Spielaktionen.
 */
export function RoomMenu({ state, api }: { state: ClientRoomState; api: RoomApi }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState<'abort' | 'leave' | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const isHost = state.hostId === state.youId;
  const inGame = state.phase !== 'lobby';

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setConfirm(null);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setConfirm(null);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="dropdown" ref={containerRef}>
      <button
        type="button"
        className="btn btn--ghost"
        onClick={() => {
          setOpen((previous) => !previous);
          setConfirm(null);
        }}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={t('menu.title')}
        title={t('menu.title')}
      >
        <IconMore />
      </button>

      {open && (
        <div className="dropdown__panel" role="dialog" aria-label={t('menu.title')}>
          <div className="stack stack--tight">
            {confirm === null && (
              <>
                {isHost && inGame && (
                  <button
                    type="button"
                    className="btn btn--block"
                    onClick={() => setConfirm('abort')}
                  >
                    {t('menu.abortGame')}
                  </button>
                )}

                <button
                  type="button"
                  className="btn btn--block btn--danger"
                  onClick={() => setConfirm('leave')}
                >
                  {t('menu.leaveRoom')}
                </button>

                <span className="text-xs text-dim">
                  {isHost ? t('menu.hostLeaveHint') : t('menu.leaveHint')}
                </span>
              </>
            )}

            {confirm !== null && (
              <>
                <span className="text-sm">
                  {confirm === 'abort' ? t('menu.confirmAbort') : t('menu.confirmLeave')}
                </span>
                <div className="row" style={{ gap: '0.5rem' }}>
                  <button
                    type="button"
                    className="btn btn--danger"
                    style={{ flex: 1 }}
                    onClick={() => {
                      setOpen(false);
                      setConfirm(null);
                      if (confirm === 'abort') api.abortGame();
                      else api.leaveRoom();
                    }}
                  >
                    {t('common.confirm')}
                  </button>
                  <button
                    type="button"
                    className="btn"
                    style={{ flex: 1 }}
                    onClick={() => setConfirm(null)}
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
